// src/pages/CrystalBallRunner.jsx
// ─── Crystal Ball Runner ── 7-Lane Rhythm Game ─────────────────────────────
// A 3D-perspective "Sky Roads / Subway Surfer" style runner where the crystal
// ball slides between 7 lanes (one per white note: C D E F G A B).
// The piano (virtual or real mic) is the controller.
// ────────────────────────────────────────────────────────────────────────────

import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from "react";
import { useNavigate } from "react-router-dom";
import FloatingPianoOverlay from "../components/FloatingPianoOverlay";
import RealPianoListener from "../components/RealPianoListener";
import { normalizeNoteName } from "../components/VirtualPiano";
import { TrophyStorage } from "../services/trophyStorage";

/* ═══════════════════════════════════════════════════════════════════════════
   §1  CONSTANTS & CONFIG
   ═══════════════════════════════════════════════════════════════════════════ */

const BPM = 72;
const BEAT_MS = (60 / BPM) * 1000; // ≈833 ms

// Canvas
const CW = 560;
const CH = 360;
const HORIZON_Y = 50;
const BALL_Y = 285;
const BALL_R = 14;
const VISIBLE_BEATS = 9;

// 7 lanes: C D E F G A B  — mapped to indices 0..6
const LANE_NOTES = ["C", "D", "E", "F", "G", "A", "B"];
const LANE_COUNT = LANE_NOTES.length;

// Track geometry
const TRACK_LEFT_NEAR = 30;
const TRACK_RIGHT_NEAR = CW - 30;
const TRACK_LEFT_FAR = CW / 2 - 55;
const TRACK_RIGHT_FAR = CW / 2 + 55;

// Hit detection: how close (in beats) the tile front must be to BALL_Y zone
const HIT_WINDOW_EARLY = 0.55; // can hit slightly early
const HIT_WINDOW_LATE = 0.35;  // small late grace

// Lives
const MAX_LIVES = 5;

// Auto-advance: if the user doesn't play for this many beats past a tile, it's a miss
const AUTO_MISS_BEATS = 1.4;

/* ─── NOTE COLORS (matching your existing palette) ──────────────────────── */
const NOTE_COLORS = {
  C: { fill: "#ef4444", dark: "#991b1b", glow: "#fca5a5", text: "#fff", lane: "#ef444440" },
  D: { fill: "#f97316", dark: "#9a3412", glow: "#fdba74", text: "#fff", lane: "#f9731640" },
  E: { fill: "#eab308", dark: "#854d0e", glow: "#fde047", text: "#1a1a1a", lane: "#eab30840" },
  F: { fill: "#22c55e", dark: "#14532d", glow: "#86efac", text: "#fff", lane: "#22c55e40" },
  G: { fill: "#06b6d4", dark: "#164e63", glow: "#67e8f9", text: "#fff", lane: "#06b6d440" },
  A: { fill: "#8b5cf6", dark: "#4c1d95", glow: "#c4b5fd", text: "#fff", lane: "#8b5cf640" },
  B: { fill: "#ec4899", dark: "#831843", glow: "#f9a8d4", text: "#fff", lane: "#ec489940" },
};

/* ─── STARS (background decorations) ────────────────────────────────────── */
const STARS = Array.from({ length: 90 }, (_, i) => ({
  x: (Math.sin(i * 137.508) * 0.5 + 0.5),
  y: (Math.cos(i * 97.201) * 0.5 + 0.5) * 0.7,
  r: 0.4 + (i % 5) * 0.3,
  a: 0.25 + (i % 7) * 0.08,
  twinkleSpeed: 0.002 + (i % 11) * 0.0005,
}));

/* ═══════════════════════════════════════════════════════════════════════════
   §2  TWINKLE TWINKLE — SONG DATA  (Level 1)
   From sheet music: ♩ = 72, 4/4 time
   Black notes = 1 beat  |  Half notes = 2 beats
   ═══════════════════════════════════════════════════════════════════════════ */

const SONG_DATA = {
  title: "Twinkle Twinkle Little Star",
  subtitle: "Doron's Lullaby",
  bpm: BPM,
  notes: [
    // ── Line 1: Twinkle Twinkle Little Star ──
    { note: "C", beats: 1 }, { note: "C", beats: 1 },
    { note: "G", beats: 1 }, { note: "G", beats: 1 },
    { note: "A", beats: 1 }, { note: "A", beats: 1 },
    { note: "G", beats: 2 },
    // ── Line 2: How I Wonder What You Are ──
    { note: "F", beats: 1 }, { note: "F", beats: 1 },
    { note: "E", beats: 1 }, { note: "E", beats: 1 },
    { note: "D", beats: 1 }, { note: "D", beats: 1 },
    { note: "C", beats: 2 },
    // ── Line 3: Up Above The World So High ──
    { note: "G", beats: 1 }, { note: "G", beats: 1 },
    { note: "F", beats: 1 }, { note: "F", beats: 1 },
    { note: "E", beats: 1 }, { note: "E", beats: 1 },
    { note: "D", beats: 2 },
    // ── Line 4: Like A Diamond In The Sky ──
    { note: "G", beats: 1 }, { note: "G", beats: 1 },
    { note: "F", beats: 1 }, { note: "F", beats: 1 },
    { note: "E", beats: 1 }, { note: "E", beats: 1 },
    { note: "D", beats: 2 },
    // ── Line 5: Twinkle Twinkle Little Star ──
    { note: "C", beats: 1 }, { note: "C", beats: 1 },
    { note: "G", beats: 1 }, { note: "G", beats: 1 },
    { note: "A", beats: 1 }, { note: "A", beats: 1 },
    { note: "G", beats: 2 },
    // ── Line 6: How I Wonder What You Are ──
    { note: "F", beats: 1 }, { note: "F", beats: 1 },
    { note: "E", beats: 1 }, { note: "E", beats: 1 },
    { note: "D", beats: 1 }, { note: "D", beats: 1 },
    { note: "C", beats: 2 },
  ],
};

const LYRICS = [
  "Twinkle, Twinkle, Little Star",
  "How I Wonder What You Are",
  "Up Above The World So High",
  "Like A Diamond In The Sky",
  "Twinkle, Twinkle, Little Star",
  "How I Wonder What You Are!",
];

/* ─── Build tile array with cumulative beat positions ───────────────────── */
function buildTiles(songNotes) {
  let pos = 3; // start a few beats ahead so tiles are visible at start
  return songNotes.map((n, i) => {
    const tile = {
      idx: i,
      note: n.note,
      beats: n.beats,
      beatStart: pos,
      lane: LANE_NOTES.indexOf(n.note),
    };
    pos += n.beats;
    return tile;
  });
}

const TILES = buildTiles(SONG_DATA.notes);
const TOTAL_TILES = TILES.length;

// Which lyric line is the tile in? (7 notes per line for Twinkle)
function getLyricForTileIdx(idx) {
  const lineIdx = Math.floor(idx / 7);
  return LYRICS[lineIdx] || "";
}

/* ═══════════════════════════════════════════════════════════════════════════
   §3  PROJECTION HELPERS — 7-lane 3D perspective
   ═══════════════════════════════════════════════════════════════════════════ */

/** Map z-distance (in beats) to depth factor t ∈ [0, 1] */
function depthT(zBeats) {
  return Math.max(0, Math.min(1, zBeats / VISIBLE_BEATS));
}

/** Get Y coordinate for a given depth */
function yAtDepth(t) {
  return BALL_Y - (BALL_Y - HORIZON_Y) * t;
}

/** Get the left and right edges of the full track at a depth */
function trackEdgesAtDepth(t) {
  const left = TRACK_LEFT_NEAR + (TRACK_LEFT_FAR - TRACK_LEFT_NEAR) * t;
  const right = TRACK_RIGHT_NEAR + (TRACK_RIGHT_FAR - TRACK_RIGHT_NEAR) * t;
  return { left, right, width: right - left };
}

/** Get the center X position of a lane (0..6) at a depth */
function laneCenterX(laneIdx, t) {
  const { left, width } = trackEdgesAtDepth(t);
  const laneWidth = width / LANE_COUNT;
  return left + laneWidth * (laneIdx + 0.5);
}

/** Get lane boundaries at a depth */
function laneBounds(laneIdx, t) {
  const { left, width } = trackEdgesAtDepth(t);
  const laneWidth = width / LANE_COUNT;
  return {
    x: left + laneWidth * laneIdx,
    w: laneWidth,
    cx: left + laneWidth * (laneIdx + 0.5),
  };
}

/* ═══════════════════════════════════════════════════════════════════════════
   §4  NOTE NORMALIZATION (reuses your VirtualPiano normalizer)
   ═══════════════════════════════════════════════════════════════════════════ */

function normalizeNote(raw) {
  if (!raw) return "";
  // Strip octave, normalize flats → sharps, uppercase
  let n = normalizeNoteName(raw);
  if (!n) {
    n = String(raw).replace(/[0-9]/g, "").toUpperCase().trim();
  }
  // Map enharmonics for white notes
  const map = { "E#": "F", "B#": "C", "CB": "B", "FB": "E" };
  return map[n] || n;
}

function noteToLane(noteName) {
  const n = normalizeNote(noteName);
  const idx = LANE_NOTES.indexOf(n);
  return idx >= 0 ? idx : -1;
}

/* ═══════════════════════════════════════════════════════════════════════════
   §5  CANVAS DRAWING FUNCTIONS
   ═══════════════════════════════════════════════════════════════════════════ */

function drawBackground(ctx, now) {
  // Sky gradient
  const sky = ctx.createLinearGradient(0, 0, 0, CH);
  sky.addColorStop(0, "#020714");
  sky.addColorStop(0.35, "#0c0630");
  sky.addColorStop(0.7, "#1a0d4a");
  sky.addColorStop(1, "#2d1566");
  ctx.fillStyle = sky;
  ctx.fillRect(0, 0, CW, CH);

  // Stars with twinkling
  STARS.forEach((s) => {
    const twinkle = 0.5 + 0.5 * Math.sin(now * s.twinkleSpeed);
    ctx.globalAlpha = s.a * twinkle;
    ctx.fillStyle = "#fff";
    ctx.beginPath();
    ctx.arc(s.x * CW, s.y * (HORIZON_Y + 20), s.r, 0, Math.PI * 2);
    ctx.fill();
  });
  ctx.globalAlpha = 1;

  // Horizon glow
  const hg = ctx.createRadialGradient(CW / 2, HORIZON_Y, 0, CW / 2, HORIZON_Y, CW * 0.5);
  hg.addColorStop(0, "rgba(100,60,220,0.35)");
  hg.addColorStop(0.5, "rgba(60,20,140,0.15)");
  hg.addColorStop(1, "rgba(60,20,140,0)");
  ctx.fillStyle = hg;
  ctx.fillRect(0, 0, CW, CH);
}

function drawTrack(ctx, now) {
  // Track floor (dark trapezoid)
  const nearL = TRACK_LEFT_NEAR;
  const nearR = TRACK_RIGHT_NEAR;
  const farL = TRACK_LEFT_FAR;
  const farR = TRACK_RIGHT_FAR;

  ctx.beginPath();
  ctx.moveTo(farL, HORIZON_Y);
  ctx.lineTo(farR, HORIZON_Y);
  ctx.lineTo(nearR, BALL_Y + 30);
  ctx.lineTo(nearL, BALL_Y + 30);
  ctx.closePath();

  const floorGrad = ctx.createLinearGradient(0, HORIZON_Y, 0, BALL_Y + 30);
  floorGrad.addColorStop(0, "rgba(15,8,40,0.95)");
  floorGrad.addColorStop(1, "rgba(25,12,60,0.85)");
  ctx.fillStyle = floorGrad;
  ctx.fill();

  // Lane dividers
  for (let i = 0; i <= LANE_COUNT; i++) {
    const tNear = 0;
    const tFar = 1;
    const xNear = trackEdgesAtDepth(tNear).left + (trackEdgesAtDepth(tNear).width / LANE_COUNT) * i;
    const xFar = trackEdgesAtDepth(tFar).left + (trackEdgesAtDepth(tFar).width / LANE_COUNT) * i;

    ctx.beginPath();
    ctx.moveTo(xFar, HORIZON_Y);
    ctx.lineTo(xNear, BALL_Y + 30);
    ctx.strokeStyle = i === 0 || i === LANE_COUNT
      ? "rgba(140,100,255,0.3)"
      : "rgba(100,70,200,0.12)";
    ctx.lineWidth = i === 0 || i === LANE_COUNT ? 1.5 : 0.8;
    ctx.stroke();
  }

  // Lane labels at bottom
  for (let i = 0; i < LANE_COUNT; i++) {
    const col = NOTE_COLORS[LANE_NOTES[i]];
    const { cx } = laneBounds(i, 0);
    ctx.save();
    ctx.font = "bold 10px 'SF Pro', 'Segoe UI', Arial, sans-serif";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillStyle = col.fill + "88";
    ctx.fillText(LANE_NOTES[i], cx, BALL_Y + 20);
    ctx.restore();
  }

  // Scrolling grid lines (depth markers)
  const beatPhase = (now / BEAT_MS) % 1;
  for (let b = 0; b < VISIBLE_BEATS; b++) {
    const z = b + beatPhase;
    const t = depthT(z);
    const y = yAtDepth(t);
    const { left, right } = trackEdgesAtDepth(t);

    ctx.beginPath();
    ctx.moveTo(left, y);
    ctx.lineTo(right, y);
    ctx.strokeStyle = `rgba(100,70,200,${0.06 * (1 - t)})`;
    ctx.lineWidth = 0.5;
    ctx.stroke();
  }
}

function drawTile(ctx, tile, currentBeat, showGuide) {
  const col = NOTE_COLORS[tile.note] || NOTE_COLORS.C;
  const lane = tile.lane;
  const zFront = tile.beatStart - currentBeat;
  const zBack = tile.beatStart + tile.beats - currentBeat;

  if (zBack < -0.5 || zFront > VISIBLE_BEATS + 1) return;

  const tFront = depthT(Math.max(0, zFront));
  const tBack = depthT(Math.max(0.02, zBack));
  const yFront = yAtDepth(tFront);
  const yBack = yAtDepth(tBack);

  const frontBounds = laneBounds(lane, tFront);
  const backBounds = laneBounds(lane, tBack);

  // Tile body (3D trapezoid)
  ctx.beginPath();
  ctx.moveTo(backBounds.x, yBack);
  ctx.lineTo(backBounds.x + backBounds.w, yBack);
  ctx.lineTo(frontBounds.x + frontBounds.w, yFront);
  ctx.lineTo(frontBounds.x, yFront);
  ctx.closePath();

  // Gradient fill
  const tg = ctx.createLinearGradient(0, yBack, 0, yFront);
  tg.addColorStop(0, col.dark + "cc");
  tg.addColorStop(1, col.fill);
  ctx.fillStyle = tg;
  ctx.fill();

  // Front edge highlight
  ctx.beginPath();
  ctx.moveTo(frontBounds.x, yFront);
  ctx.lineTo(frontBounds.x + frontBounds.w, yFront);
  ctx.strokeStyle = col.glow;
  ctx.lineWidth = 2;
  ctx.stroke();

  // Side edges
  ctx.beginPath();
  ctx.moveTo(backBounds.x, yBack);
  ctx.lineTo(frontBounds.x, yFront);
  ctx.moveTo(backBounds.x + backBounds.w, yBack);
  ctx.lineTo(frontBounds.x + frontBounds.w, yFront);
  ctx.strokeStyle = col.dark + "66";
  ctx.lineWidth = 0.8;
  ctx.stroke();

  // Note label on tile (training guide)
  if (showGuide && zFront > 0.3 && zFront < VISIBLE_BEATS - 1) {
    const midZ = (Math.max(0.1, zFront) + zBack) / 2;
    const tMid = depthT(midZ);
    const midBounds = laneBounds(lane, tMid);
    const yMid = yAtDepth(tMid);
    const fs = Math.max(9, Math.min(18, midBounds.w * 0.55));

    ctx.save();
    ctx.font = `900 ${fs}px 'SF Pro', Arial, sans-serif`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillStyle = "rgba(0,0,0,0.4)";
    ctx.fillText(tile.note, midBounds.cx + 0.5, yMid + 0.5);
    ctx.fillStyle = col.text;
    ctx.fillText(tile.note, midBounds.cx, yMid);
    ctx.restore();
  }

  // HOLD label for long tiles
  if (tile.beats > 1 && zFront > 0.2 && zFront < VISIBLE_BEATS - 0.5) {
    const midZ = (Math.max(0.1, zFront) + zBack) / 2;
    const tMid = depthT(midZ);
    const midBounds = laneBounds(lane, tMid);
    const yMid = yAtDepth(tMid);
    const fs = Math.max(7, midBounds.w * 0.22);

    ctx.save();
    ctx.font = `bold ${fs}px 'SF Pro', Arial, sans-serif`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillStyle = "rgba(255,255,255,0.45)";
    ctx.fillText("HOLD", midBounds.cx, yMid + fs * 1.3);
    ctx.restore();
  }
}

function drawBall(ctx, ballLane, ballY, glowIntensity, opacity, now) {
  const t = depthT(0); // ball is at depth 0 (front)
  const cx = laneCenterX(ballLane, t);
  const cy = ballY;

  ctx.save();
  ctx.globalAlpha = opacity;

  // Shadow on track
  const shadowScale = Math.max(0.2, 1 - Math.abs(cy - BALL_Y) * 0.004);
  ctx.beginPath();
  ctx.ellipse(cx, BALL_Y + 6, BALL_R * 1.2 * shadowScale, BALL_R * 0.3 * shadowScale, 0, 0, Math.PI * 2);
  ctx.fillStyle = "rgba(0,0,0,0.35)";
  ctx.fill();

  // Glow ring
  const glow = ctx.createRadialGradient(cx, cy, BALL_R * 0.4, cx, cy, BALL_R * 2.8);
  glow.addColorStop(0, `rgba(147,197,253,${0.35 * glowIntensity})`);
  glow.addColorStop(1, "rgba(147,197,253,0)");
  ctx.fillStyle = glow;
  ctx.beginPath();
  ctx.arc(cx, cy, BALL_R * 2.8, 0, Math.PI * 2);
  ctx.fill();

  // Ball body — crystal effect
  const body = ctx.createRadialGradient(cx - 4, cy - 4, 1, cx, cy, BALL_R);
  body.addColorStop(0, "#ffffff");
  body.addColorStop(0.15, "#e0f2fe");
  body.addColorStop(0.5, "#7dd3fc");
  body.addColorStop(0.85, "#3b82f6");
  body.addColorStop(1, "#1d4ed8");
  ctx.beginPath();
  ctx.arc(cx, cy, BALL_R, 0, Math.PI * 2);
  ctx.fillStyle = body;
  ctx.fill();

  // Rim light
  ctx.strokeStyle = "rgba(255,255,255,0.6)";
  ctx.lineWidth = 1.5;
  ctx.stroke();

  // Specular highlight
  ctx.beginPath();
  ctx.arc(cx - 4, cy - 4, 4.5, 0, Math.PI * 2);
  ctx.fillStyle = "rgba(255,255,255,0.8)";
  ctx.fill();

  ctx.restore();
}

function drawHitZone(ctx) {
  // Subtle horizontal line at ball Y
  ctx.save();
  ctx.setLineDash([3, 5]);
  ctx.strokeStyle = "rgba(255,255,255,0.08)";
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(TRACK_LEFT_NEAR, BALL_Y);
  ctx.lineTo(TRACK_RIGHT_NEAR, BALL_Y);
  ctx.stroke();
  ctx.setLineDash([]);
  ctx.restore();
}

function drawSparkles(ctx, x, y, count, col, now) {
  for (let i = 0; i < count; i++) {
    const angle = (i / count) * Math.PI * 2 + now * 0.003;
    const dist = BALL_R * 2 + Math.sin(now * 0.006 + i * 1.5) * 6;
    const sx = x + Math.cos(angle) * dist;
    const sy = y + Math.sin(angle) * dist;
    ctx.beginPath();
    ctx.arc(sx, sy, 1.8, 0, Math.PI * 2);
    ctx.fillStyle = col || "#fde047";
    ctx.globalAlpha = 0.5 + Math.sin(now * 0.008 + i) * 0.35;
    ctx.fill();
  }
  ctx.globalAlpha = 1;
}

function drawMissFlash(ctx, intensity) {
  if (intensity <= 0) return;
  ctx.save();
  ctx.globalAlpha = intensity * 0.35;
  ctx.fillStyle = "#ef4444";
  ctx.fillRect(0, 0, CW, CH);
  ctx.restore();
}

function drawComboText(ctx, combo, now) {
  if (combo < 3) return;
  const pulse = 1 + 0.08 * Math.sin(now * 0.008);
  ctx.save();
  ctx.font = `900 ${14 * pulse}px 'SF Pro', Arial, sans-serif`;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillStyle = "#fde047";
  ctx.globalAlpha = 0.8;
  ctx.fillText(`${combo}x COMBO!`, CW / 2, HORIZON_Y + 25);
  ctx.restore();
}

/* ═══════════════════════════════════════════════════════════════════════════
   §6  BALL ANIMATION: smooth lane sliding with interpolation
   ═══════════════════════════════════════════════════════════════════════════ */

function lerpLane(from, to, progress) {
  return from + (to - from) * Math.min(1, progress);
}

/* ═══════════════════════════════════════════════════════════════════════════
   §7  MAIN GAME COMPONENT
   ═══════════════════════════════════════════════════════════════════════════ */

export default function CrystalBallRunner() {
  const navigate = useNavigate();
  const canvasRef = useRef(null);
  const rafRef = useRef(null);

  // ─── Game states ───────────────────────────────────────────────────────
  const [phase, setPhase] = useState("menu"); // 'menu' | 'playing' | 'gameover' | 'complete'
  const [lives, setLives] = useState(MAX_LIVES);
  const [score, setScore] = useState(0);
  const [combo, setCombo] = useState(0);
  const [maxCombo, setMaxCombo] = useState(0);
  const [showGuide, setShowGuide] = useState(true);

  // Piano UI
  const [pianoOpen, setPianoOpen] = useState(true);
  const [pianoPrefs, setPianoPrefs] = useState({ size: "l", x: null, y: null });
  const [showKeyGlow, setShowKeyGlow] = useState(true);
  const [inputMode, setInputMode] = useState("virtual"); // 'virtual' | 'mic'
  const [isListening, setIsListening] = useState(false);
  const [windowSize, setWindowSize] = useState(() => ({
    width: typeof window !== "undefined" ? window.innerWidth : 1024,
    height: typeof window !== "undefined" ? window.innerHeight : 768,
  }));

  // Doron emotion
  const [doronEmotion, setDoronEmotion] = useState("awake");

  // ─── Game engine ref (mutable, not in React state for perf) ──────────
  const gs = useRef({
    // Timing
    currentBeat: 0,
    startTime: null,

    // Progress
    nextTileIdx: 0,  // which tile is the user trying to hit next
    matchedCount: 0,

    // Ball
    ballLane: 3,       // start in middle (F lane)
    ballLaneFrom: 3,
    ballLaneTo: 3,
    laneSlideStart: 0,
    laneSlideMs: 120,  // 120ms to slide between lanes

    // Ball bounce
    ballAnim: "idle",
    ballDrawY: BALL_Y,
    animStartTime: 0,
    bounceHeight: 30,

    // Effects
    missFlash: 0,
    hitSparkles: 0,
    hitSparkleColor: "#fde047",
    combo: 0,
    maxCombo: 0,

    // Lives/score
    lives: MAX_LIVES,
    score: 0,

    // State
    phase: "menu",

    // Doron
    doronState: "awake",
  });

  // ─── Window resize ──────────────────────────────────────────────────────
  useEffect(() => {
    const onResize = () => setWindowSize({
      width: window.innerWidth,
      height: window.innerHeight,
    });
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // ─── START GAME ────────────────────────────────────────────────────────
  const startGame = useCallback(() => {
    const g = gs.current;
    g.currentBeat = 0;
    g.startTime = performance.now();
    g.nextTileIdx = 0;
    g.matchedCount = 0;
    g.ballLane = 3; // F
    g.ballLaneFrom = 3;
    g.ballLaneTo = 3;
    g.laneSlideStart = 0;
    g.ballAnim = "idle";
    g.ballDrawY = BALL_Y;
    g.missFlash = 0;
    g.hitSparkles = 0;
    g.combo = 0;
    g.maxCombo = 0;
    g.lives = MAX_LIVES;
    g.score = 0;
    g.phase = "playing";
    g.doronState = "awake";

    setPhase("playing");
    setLives(MAX_LIVES);
    setScore(0);
    setCombo(0);
    setMaxCombo(0);
    setDoronEmotion("awake");
    setPianoOpen(true);

    TrophyStorage.startRound?.();
  }, []);

  // ─── HANDLE NOTE INPUT (from either piano or mic) ──────────────────────
  const handleNoteInput = useCallback((noteName) => {
    const g = gs.current;
    if (g.phase !== "playing") return;
    if (g.nextTileIdx >= TOTAL_TILES) return;

    const lane = noteToLane(noteName);
    if (lane < 0) return; // ignore black keys or invalid

    const now = performance.now();

    // Slide ball to the played lane
    g.ballLaneFrom = g.ballLane;
    g.ballLaneTo = lane;
    g.laneSlideStart = now;

    // Check if this note matches the expected tile and is within the hit window
    const tile = TILES[g.nextTileIdx];
    const tileFrontBeat = tile.beatStart;
    const currentBeat = g.currentBeat;
    const beatDiff = tileFrontBeat - currentBeat;

    // Is the tile in the hit window?
    const inWindow = beatDiff >= -HIT_WINDOW_LATE && beatDiff <= HIT_WINDOW_EARLY;

    if (tile.note === LANE_NOTES[lane] && inWindow) {
      // ✅ HIT!
      g.nextTileIdx++;
      g.matchedCount++;
      g.combo++;
      if (g.combo > g.maxCombo) g.maxCombo = g.combo;
      g.score += 100 + g.combo * 10;

      // Ball bounce
      g.ballAnim = "bounce";
      g.ballDrawY = BALL_Y;
      g.animStartTime = now;

      // Sparkles
      g.hitSparkles = 8;
      g.hitSparkleColor = (NOTE_COLORS[tile.note] || NOTE_COLORS.C).glow;

      // Update React states
      setScore(g.score);
      setCombo(g.combo);
      setMaxCombo(g.maxCombo);

      // Doron sleepiness
      const pct = g.matchedCount / TOTAL_TILES;
      if (pct >= 1) {
        g.doronState = "asleep";
        setDoronEmotion("asleep");
      } else if (pct >= 0.75) {
        g.doronState = "sleepy";
        setDoronEmotion("sleepy");
      } else if (pct >= 0.4) {
        g.doronState = "groggy";
        setDoronEmotion("groggy");
      }

      // Check completion
      if (g.matchedCount >= TOTAL_TILES) {
        g.phase = "complete";
        g.doronState = "asleep";
        setPhase("complete");
        setDoronEmotion("asleep");
        TrophyStorage.markLevelComplete?.(1);
      }
    } else if (!inWindow && beatDiff < -HIT_WINDOW_LATE) {
      // Too late — the tile already passed, this is a wrong input
      registerMiss(g, now);
    } else if (tile.note !== LANE_NOTES[lane] && inWindow) {
      // Wrong note but in the right timing zone
      registerMiss(g, now);
    }
    // else: too early, just move the ball (no penalty for moving early)
  }, []);

  function registerMiss(g, now) {
    g.missFlash = 1.0;
    g.combo = 0;
    g.lives--;
    g.ballAnim = "fall";
    g.ballDrawY = BALL_Y;
    g.animStartTime = now;

    setCombo(0);
    setLives(g.lives);

    // Fire effect on Doron
    if (g.doronState !== "asleep") {
      g.doronState = "fire";
      setDoronEmotion("fire");
      setTimeout(() => {
        if (gs.current.doronState === "fire") {
          const pct = gs.current.matchedCount / TOTAL_TILES;
          gs.current.doronState = pct >= 0.75 ? "sleepy" : pct >= 0.4 ? "groggy" : "awake";
          setDoronEmotion(gs.current.doronState);
        }
      }, 1500);
    }

    if (g.lives <= 0) {
      g.phase = "gameover";
      setPhase("gameover");
    }
  }

  // ─── Virtual piano handler ─────────────────────────────────────────────
  const handleVirtualKey = useCallback((noteName) => {
    if (inputMode === "mic" && isListening) {
      // Switch to virtual mode, lock mic
      setInputMode("virtual");
      setIsListening(false);
    }
    handleNoteInput(noteName);
  }, [handleNoteInput, inputMode, isListening]);

  // ─── Mic detection handler ─────────────────────────────────────────────
  const handleMicNote = useCallback((note) => {
    handleNoteInput(note);
  }, [handleNoteInput]);

  // Build a flat melody array for RealPianoListener
  const micTargetMelody = useMemo(() => {
    return SONG_DATA.notes.map((n) => n.note);
  }, []);

  // ─── MAIN RENDER LOOP ─────────────────────────────────────────────────
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");

    function frame(now) {
      rafRef.current = requestAnimationFrame(frame);
      const g = gs.current;

      // ── Advance clock ────────────────────────────────────────────────
      if (g.phase === "playing" && g.startTime) {
        g.currentBeat = (now - g.startTime) / BEAT_MS;
      }

      // ── Auto-miss: if a tile has scrolled past without being hit ──
      if (g.phase === "playing" && g.nextTileIdx < TOTAL_TILES) {
        const tile = TILES[g.nextTileIdx];
        const beatDiff = tile.beatStart - g.currentBeat;
        if (beatDiff < -AUTO_MISS_BEATS) {
          registerMiss(g, now);
          g.nextTileIdx++; // skip this tile
        }
      }

      // ── Decay effects ────────────────────────────────────────────────
      if (g.missFlash > 0) g.missFlash = Math.max(0, g.missFlash - 0.03);
      if (g.hitSparkles > 0) g.hitSparkles = Math.max(0, g.hitSparkles - 0.12);

      // ── Ball lane interpolation ──────────────────────────────────────
      const slideElapsed = now - g.laneSlideStart;
      const slideProgress = Math.min(1, slideElapsed / g.laneSlideMs);
      // Ease out
      const eased = 1 - Math.pow(1 - slideProgress, 3);
      g.ballLane = lerpLane(g.ballLaneFrom, g.ballLaneTo, eased);

      // ── Ball Y animation ─────────────────────────────────────────────
      let ballDrawY = BALL_Y;
      let ballOpacity = 1;
      let glowIntensity = 1;

      if (g.ballAnim === "bounce") {
        const elapsed = now - g.animStartTime;
        const progress = Math.min(1, elapsed / (BEAT_MS * 0.6));
        ballDrawY = BALL_Y - g.bounceHeight * Math.sin(progress * Math.PI);
        glowIntensity = 1 + 0.6 * Math.sin(progress * Math.PI);
        if (progress >= 1) {
          g.ballAnim = "idle";
        }
      } else if (g.ballAnim === "fall") {
        const elapsed = now - g.animStartTime;
        const progress = elapsed / 550;
        ballDrawY = BALL_Y + progress * progress * 60;
        ballOpacity = Math.max(0, 1 - progress * 0.7);
        glowIntensity = 0.3;
        if (elapsed > 800) {
          g.ballAnim = "recover";
          g.animStartTime = now;
        }
      } else if (g.ballAnim === "recover") {
        const elapsed = now - g.animStartTime;
        const progress = Math.min(1, elapsed / 350);
        ballDrawY = BALL_Y - 40 + 40 * progress;
        ballOpacity = progress;
        if (progress >= 1) {
          g.ballAnim = "idle";
        }
      } else {
        // Idle: gentle floating
        ballDrawY = BALL_Y - 2.5 * Math.sin(now * 0.003);
      }

      // ── DRAW ─────────────────────────────────────────────────────────
      ctx.clearRect(0, 0, CW, CH);
      drawBackground(ctx, now);
      drawTrack(ctx, now);
      drawHitZone(ctx);

      // Draw tiles (far to near for correct occlusion)
      const currentBeat = g.currentBeat;
      for (let i = TOTAL_TILES - 1; i >= 0; i--) {
        const tile = TILES[i];
        const zFront = tile.beatStart - currentBeat;
        const zBack = tile.beatStart + tile.beats - currentBeat;
        if (zBack < -1 || zFront > VISIBLE_BEATS + 1) continue;

        // Grey out already-hit tiles
        if (i < g.nextTileIdx) {
          ctx.save();
          ctx.globalAlpha = 0.15;
          drawTile(ctx, tile, currentBeat, false);
          ctx.restore();
        } else {
          drawTile(ctx, tile, currentBeat, showGuide);

          // Pulse the NEXT tile
          if (i === g.nextTileIdx && zFront > -0.5 && zFront < 2.0) {
            const pulseAlpha = 0.15 + 0.1 * Math.sin(now * 0.006);
            const tFront = depthT(Math.max(0, zFront));
            const tBack = depthT(Math.max(0.02, zBack));
            const yFront = yAtDepth(tFront);
            const yBack = yAtDepth(tBack);
            const fb = laneBounds(tile.lane, tFront);
            const bb = laneBounds(tile.lane, tBack);
            const col = NOTE_COLORS[tile.note] || NOTE_COLORS.C;

            ctx.save();
            ctx.globalAlpha = pulseAlpha;
            ctx.beginPath();
            ctx.moveTo(bb.x, yBack);
            ctx.lineTo(bb.x + bb.w, yBack);
            ctx.lineTo(fb.x + fb.w, yFront);
            ctx.lineTo(fb.x, yFront);
            ctx.closePath();
            ctx.fillStyle = col.glow;
            ctx.fill();
            ctx.restore();
          }
        }
      }

      // Miss flash
      drawMissFlash(ctx, g.missFlash);

      // Sparkles
      if (g.hitSparkles > 0) {
        ctx.save();
        ctx.globalAlpha = g.hitSparkles / 8;
        const ballCx = laneCenterX(g.ballLane, 0);
        drawSparkles(ctx, ballCx, BALL_Y, 10, g.hitSparkleColor, now);
        ctx.restore();
        ctx.globalAlpha = 1;
      }

      // Ball
      drawBall(ctx, g.ballLane, ballDrawY, glowIntensity, ballOpacity, now);

      // Combo text
      drawComboText(ctx, g.combo, now);

      // ── Overlays ─────────────────────────────────────────────────────
      if (g.phase === "menu") {
        ctx.save();
        ctx.globalAlpha = 0.75;
        ctx.fillStyle = "#050218";
        ctx.fillRect(0, 0, CW, CH);
        ctx.globalAlpha = 1;

        ctx.font = "bold 22px 'SF Pro', Arial, sans-serif";
        ctx.fillStyle = "#c4b5fd";
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillText("🔮 Crystal Ball Runner", CW / 2, CH / 2 - 30);

        ctx.font = "14px 'SF Pro', Arial, sans-serif";
        ctx.fillStyle = "#a78bfa";
        ctx.fillText("Play the right notes to keep the ball rolling!", CW / 2, CH / 2 + 5);

        ctx.font = "bold 13px 'SF Pro', Arial, sans-serif";
        ctx.fillStyle = "#8b5cf6";
        ctx.fillText("Press Start below", CW / 2, CH / 2 + 35);
        ctx.restore();
      }

      if (g.phase === "gameover") {
        ctx.save();
        ctx.globalAlpha = 0.7;
        ctx.fillStyle = "#1a0000";
        ctx.fillRect(0, 0, CW, CH);
        ctx.globalAlpha = 1;

        ctx.font = "bold 26px 'SF Pro', Arial, sans-serif";
        ctx.fillStyle = "#ef4444";
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillText("🔥 Doron Woke Up!", CW / 2, CH / 2 - 15);

        ctx.font = "14px 'SF Pro', Arial, sans-serif";
        ctx.fillStyle = "#fca5a5";
        ctx.fillText(`Score: ${g.score}  |  Max Combo: ${g.maxCombo}x`, CW / 2, CH / 2 + 18);
        ctx.restore();
      }

      if (g.phase === "complete") {
        ctx.save();
        ctx.globalAlpha = 0.55;
        ctx.fillStyle = "#1a0040";
        ctx.fillRect(0, 0, CW, CH);
        ctx.globalAlpha = 1;

        ctx.font = "bold 24px 'SF Pro', Arial, sans-serif";
        ctx.fillStyle = "#fde047";
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillText("✨ Doron is Sleeping! ✨", CW / 2, CH / 2 - 15);

        ctx.font = "14px 'SF Pro', Arial, sans-serif";
        ctx.fillStyle = "#c4b5fd";
        ctx.fillText(`Score: ${g.score}  |  Max Combo: ${g.maxCombo}x`, CW / 2, CH / 2 + 18);
        ctx.restore();
      }
    }

    rafRef.current = requestAnimationFrame(frame);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [phase, showGuide]);

  // ─── DORON IMAGE ───────────────────────────────────────────────────────
  const doronSrc = doronEmotion === "asleep"
    ? "/images/sleeping-doron.png"
    : "/images/DORON.png";

  const doronStyle = useMemo(() => {
    const base = { transition: "all 0.4s ease", filter: "drop-shadow(0 4px 12px rgba(0,0,0,0.4))" };
    if (doronEmotion === "fire") {
      return { ...base, filter: "drop-shadow(0 0 18px #ef4444) drop-shadow(0 4px 8px rgba(0,0,0,0.3))" };
    }
    if (doronEmotion === "asleep") {
      return { ...base, filter: "drop-shadow(0 0 14px #8b5cf6)", opacity: 0.85 };
    }
    return base;
  }, [doronEmotion]);

  const sleepLevel = Math.floor((gs.current.matchedCount / TOTAL_TILES) * 4);

  // ─── Current lyric ─────────────────────────────────────────────────────
  const currentLyric = getLyricForTileIdx(
    Math.min(gs.current.nextTileIdx, TOTAL_TILES - 1)
  );

  // ─── Next expected note ────────────────────────────────────────────────
  const nextNote = gs.current.nextTileIdx < TOTAL_TILES
    ? TILES[gs.current.nextTileIdx].note
    : null;

  return (
    <div
      className="min-h-screen flex flex-col items-center justify-start select-none"
      style={{
        background: "linear-gradient(180deg, #050218 0%, #0f0736 40%, #1e0f52 100%)",
      }}
    >
      {/* ═══ TOP BAR ════════════════════════════════════════════════════ */}
      <div className="w-full max-w-[600px] px-3 pt-3 pb-1 flex items-center justify-between">
        <button
          onClick={() => navigate(-1)}
          className="px-3 py-1.5 rounded-full text-xs font-bold bg-white/10 text-white/70 hover:bg-white/20 transition border border-white/10"
        >
          ← Back
        </button>

        <div className="text-center">
          <div className="text-sm font-black text-purple-200">
            🔮 Crystal Ball Runner
          </div>
          <div className="text-[10px] text-purple-400">
            {SONG_DATA.title}
          </div>
        </div>

        <button
          onClick={() => setShowGuide((v) => !v)}
          className={
            "px-2.5 py-1.5 rounded-full text-[10px] font-bold border transition " +
            (showGuide
              ? "bg-blue-600/80 border-blue-400 text-white"
              : "bg-white/10 border-white/20 text-white/60")
          }
        >
          🎯 {showGuide ? "ON" : "OFF"}
        </button>
      </div>

      {/* ═══ STATS BAR ══════════════════════════════════════════════════ */}
      <div className="w-full max-w-[600px] px-3 py-1 flex items-center justify-between text-[11px] font-bold">
        {/* Lives */}
        <div className="flex items-center gap-1">
          {Array.from({ length: MAX_LIVES }).map((_, i) => (
            <span
              key={i}
              className={i < lives ? "text-red-400" : "text-white/15"}
              style={{ fontSize: 14 }}
            >
              ♥
            </span>
          ))}
        </div>

        {/* Score */}
        <div className="text-purple-200">
          Score: <span className="text-white font-black">{score}</span>
        </div>

        {/* Combo */}
        <div className="text-purple-300">
          {combo >= 2 && (
            <span className="text-yellow-300 font-black">{combo}x </span>
          )}
          Combo
        </div>

        {/* Progress */}
        <div className="text-purple-300">
          {gs.current.matchedCount}/{TOTAL_TILES}
        </div>
      </div>

      {/* ═══ GAME CANVAS ════════════════════════════════════════════════ */}
      <div className="w-full max-w-[600px] px-2">
        <canvas
          ref={canvasRef}
          width={CW}
          height={CH}
          className="w-full rounded-2xl border border-purple-800/40 shadow-2xl"
          style={{ background: "#050218" }}
        />
      </div>

      {/* ═══ LYRIC + NEXT NOTE ══════════════════════════════════════════ */}
      {phase === "playing" && (
        <div className="w-full max-w-[600px] px-3 py-2 flex items-center justify-between">
          <div className="text-xs text-purple-300 italic truncate max-w-[60%]">
            ♫ {currentLyric}
          </div>
          {nextNote && (
            <div className="flex items-center gap-1.5">
              <span className="text-[10px] text-purple-400">Next:</span>
              <span
                className="px-2.5 py-0.5 rounded-full text-xs font-black"
                style={{
                  background: (NOTE_COLORS[nextNote] || NOTE_COLORS.C).fill,
                  color: (NOTE_COLORS[nextNote] || NOTE_COLORS.C).text,
                }}
              >
                {nextNote}
              </span>
            </div>
          )}
        </div>
      )}

      {/* ═══ DORON AREA ═════════════════════════════════════════════════ */}
      <div
        className="w-full max-w-[600px] px-4 flex items-end justify-between"
        style={{
          background: "linear-gradient(to bottom, #1e0f52, #0a0320)",
          borderRadius: "0 0 20px 20px",
          minHeight: 90,
          marginTop: -2,
        }}
      >
        {/* Status */}
        <div className="flex flex-col gap-1 pt-2 pb-2">
          <div className="text-[10px] font-bold text-purple-300">
            {doronEmotion === "fire"
              ? "🔥 MISS! Doron stirs!"
              : doronEmotion === "asleep"
              ? "💤 Fast asleep..."
              : doronEmotion === "sleepy"
              ? "😪 Getting sleepy..."
              : doronEmotion === "groggy"
              ? "😑 Drowsy..."
              : "😤 Wide awake!"}
          </div>

          {/* Sleep bar */}
          <div className="w-28 h-2 rounded-full bg-purple-900/60 overflow-hidden border border-purple-700/40">
            <div
              className="h-full rounded-full transition-all duration-500"
              style={{
                width: `${(gs.current.matchedCount / TOTAL_TILES) * 100}%`,
                background: doronEmotion === "fire"
                  ? "#ef4444"
                  : "linear-gradient(to right, #8b5cf6, #c4b5fd)",
              }}
            />
          </div>

          {/* zZZ */}
          {sleepLevel >= 1 && (
            <div className="flex gap-0.5">
              {Array.from({ length: sleepLevel }).map((_, i) => (
                <span
                  key={i}
                  className="text-purple-300 font-black"
                  style={{
                    fontSize: 9 + i * 2.5,
                    opacity: 0.4 + i * 0.2,
                    animation: `bounce ${1 + i * 0.3}s ease-in-out infinite`,
                    animationDelay: `${i * 0.25}s`,
                  }}
                >
                  z
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Doron */}
        <div className="relative pb-1">
          <img
            src={doronSrc}
            alt="Doron the Dragon"
            className="h-16 sm:h-20 w-auto object-contain"
            style={doronStyle}
            onError={(e) => { e.currentTarget.style.display = "none"; }}
            draggable={false}
          />
          {doronEmotion === "fire" && (
            <div
              className="absolute -top-5 left-1/2 -translate-x-1/2 text-xl"
              style={{ animation: "pulse 0.3s ease-in-out infinite" }}
            >
              🔥
            </div>
          )}
          {doronEmotion === "asleep" && (
            <div
              className="absolute -top-6 right-0 text-purple-300 font-black text-base"
              style={{ animation: "bounce 1.5s ease-in-out infinite" }}
            >
              💤
            </div>
          )}
        </div>
      </div>

      {/* ═══ COLOR LEGEND ═══════════════════════════════════════════════ */}
      {phase === "playing" && (
        <div className="w-full max-w-[600px] px-3 py-2 flex items-center justify-center gap-1 flex-wrap">
          {LANE_NOTES.map((n) => {
            const col = NOTE_COLORS[n];
            const isNext = n === nextNote;
            return (
              <span
                key={n}
                className={
                  "px-2 py-0.5 rounded text-[10px] font-black transition-all " +
                  (isNext ? "ring-2 ring-white/60 scale-110" : "")
                }
                style={{ background: col.fill, color: col.text }}
              >
                {n}
              </span>
            );
          })}
        </div>
      )}

      {/* ═══ ACTION BUTTONS ═════════════════════════════════════════════ */}
      <div className="w-full max-w-[600px] px-3 py-3 flex items-center justify-center gap-3">
        {phase === "menu" && (
          <button
            onClick={startGame}
            className="px-8 py-3 rounded-full bg-gradient-to-b from-purple-500 to-purple-700 text-white font-black text-sm shadow-lg hover:from-purple-400 hover:to-purple-600 transition-all active:scale-95"
          >
            🎮 Start Game
          </button>
        )}

        {phase === "gameover" && (
          <button
            onClick={startGame}
            className="px-8 py-3 rounded-full bg-gradient-to-b from-red-500 to-red-700 text-white font-black text-sm shadow-lg hover:from-red-400 hover:to-red-600 transition-all active:scale-95"
          >
            🔄 Try Again
          </button>
        )}

        {phase === "complete" && (
          <div className="flex gap-3">
            <button
              onClick={() => navigate("/level-2")}
              className="px-6 py-3 rounded-full bg-gradient-to-b from-purple-500 to-purple-700 text-white font-black text-sm shadow-lg hover:from-purple-400 hover:to-purple-600 transition-all active:scale-95"
            >
              Next Level →
            </button>
            <button
              onClick={startGame}
              className="px-6 py-3 rounded-full border-2 border-purple-400/40 text-purple-300 font-bold text-sm hover:bg-purple-900/30 transition-all"
            >
              🔄 Replay
            </button>
          </div>
        )}

        {phase === "playing" && (
          <div className="flex gap-2">
            <button
              onClick={() => {
                setInputMode("mic");
                setIsListening(true);
              }}
              className={
                "px-4 py-2 rounded-full text-xs font-bold border transition " +
                (inputMode === "mic" && isListening
                  ? "bg-green-600/80 border-green-400 text-white"
                  : "bg-white/10 border-white/20 text-white/60 hover:bg-white/20")
              }
            >
              🎤 {inputMode === "mic" && isListening ? "Listening..." : "Use Mic"}
            </button>
            <button
              onClick={startGame}
              className="px-4 py-2 rounded-full text-xs font-bold bg-white/10 border border-white/20 text-white/60 hover:bg-white/20 transition"
            >
              🔄 Restart
            </button>
          </div>
        )}
      </div>

      {/* ═══ FLOATING PIANO OVERLAY ═════════════════════════════════════ */}
      <FloatingPianoOverlay
        open={pianoOpen && phase === "playing"}
        windowSize={windowSize}
        hasStarted={phase === "playing"}
        firstKey="C4"
        lastKey="B4"
        pianoConfig={{ keyWidth: 38, whiteKeyHeight: 150, blackKeyWidth: 24, blackKeyHeight: 96 }}
        onNoteClick={handleVirtualKey}
        showKeyGlow={showKeyGlow}
        onToggleKeyGlow={setShowKeyGlow}
        onDock={() => setPianoOpen(false)}
        initialPrefs={pianoPrefs}
        onPrefsChange={setPianoPrefs}
        showAllBlackLabels={false}
      />

      {/* Piano re-open button if docked */}
      {!pianoOpen && phase === "playing" && (
        <button
          onClick={() => setPianoOpen(true)}
          className="fixed bottom-4 right-4 z-[4999] px-3 py-2 rounded-full bg-purple-600/90 text-white text-xs font-bold shadow-lg border border-purple-400/40 hover:bg-purple-500 transition"
        >
          🎹 Show Piano
        </button>
      )}

      {/* ═══ REAL PIANO LISTENER (invisible, always mounted when mic mode) ═══ */}
      {inputMode === "mic" && isListening && phase === "playing" && (
        <div className="hidden">
          <RealPianoListener
            targetMelody={micTargetMelody}
            onNoteDetected={handleMicNote}
            onNoteMatched={() => {}}
            onComplete={() => {}}
            onWrongConfirmed={() => {}}
            autoStart={true}
            hideStartButton={true}
            showMicHint={false}
            containerClassName="hidden"
            toleranceCents={85}
            toleranceHz={14}
            detectionSensitivity={1.25}
          />
        </div>
      )}

      {/* ═══ COMPLETION MODAL ═══════════════════════════════════════════ */}
      {phase === "complete" && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 px-4">
          <div className="bg-white rounded-[28px] shadow-2xl max-w-sm w-full p-6 flex flex-col items-center gap-4 text-center">
            <div className="text-5xl">⭐</div>
            <h2 className="text-xl font-black text-slate-900">
              Doron is Fast Asleep!
            </h2>
            <p className="text-sm text-slate-600">
              You played all {TOTAL_TILES} notes and put Doron to sleep with
              Twinkle Twinkle! Score: <span className="font-black text-purple-600">{score}</span>
              {maxCombo >= 3 && (
                <> — Best combo: <span className="font-black text-yellow-600">{maxCombo}x</span></>
              )}
            </p>
            <img
              src="/images/sleeping-doron.png"
              alt="Sleeping Doron"
              className="h-20 w-auto object-contain"
              onError={(e) => { e.currentTarget.style.display = "none"; }}
            />
            <div className="flex gap-3 w-full">
              <button
                type="button"
                className="flex-1 px-4 py-3 rounded-full bg-purple-600 text-white font-extrabold shadow-lg hover:bg-purple-700 transition"
                onClick={() => navigate("/level-2")}
              >
                Next Level →
              </button>
              <button
                type="button"
                className="px-4 py-3 rounded-full border-2 border-slate-200 text-slate-700 font-bold hover:bg-slate-50 transition"
                onClick={startGame}
              >
                Replay
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ═══ GAME OVER MODAL ════════════════════════════════════════════ */}
      {phase === "gameover" && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 px-4">
          <div className="bg-white rounded-[28px] shadow-2xl max-w-sm w-full p-6 flex flex-col items-center gap-4 text-center">
            <div className="text-5xl">🔥</div>
            <h2 className="text-xl font-black text-slate-900">
              Doron Woke Up!
            </h2>
            <p className="text-sm text-slate-600">
              You got {gs.current.matchedCount} of {TOTAL_TILES} notes right.
              Score: <span className="font-black text-purple-600">{score}</span>.
              Try again — you can do this!
            </p>
            <img
              src="/images/DORON.png"
              alt="Angry Doron"
              className="h-20 w-auto object-contain"
              onError={(e) => { e.currentTarget.style.display = "none"; }}
            />
            <button
              type="button"
              className="w-full px-4 py-3 rounded-full bg-red-500 text-white font-extrabold shadow-lg hover:bg-red-600 transition"
              onClick={startGame}
            >
              🔄 Try Again
            </button>
          </div>
        </div>
      )}
    </div>
  );
}